import { Routes } from '@angular/router';
import { authGuard } from './core/guards/auth.guard';
import { emailVerifiedGuard } from './core/guards/email-verified-guard';
import { AuthLayoutComponent } from './layouts/auth-layout/auth-layout.component';
import { AppLayoutComponent } from './layouts/app-layout/app-layout.component';

export const routes: Routes = [
// Root redirect to dashboard
{ path: '', redirectTo: '/dashboard', pathMatch: 'full' },

  // Auth routes (lazy loaded)
  {
    path: '',
    component: AuthLayoutComponent,
    children: [
      {
        path: 'login',
        loadComponent: () => import('./features/auth/login/login.component').then(m => m.LoginComponent),
        data: { title: 'Login' }
      },
      {
        path: 'register',
        loadComponent: () => import('./features/auth/register/register').then(m => m.RegisterComponent),
        data: { title: 'Create account' }
      },
      {
        path: 'auth/social/callback',
        loadComponent: () => import('./features/auth/social-callback/social-callback').then(m => m.SocialCallbackComponent),
        data: { title: 'Social sign-in' }
      },
      {
        path: 'invitations/accept/:token',
        loadComponent: () => import('./features/auth/invitation-entry/invitation-entry').then(m => m.InvitationEntryComponent),
        data: { title: 'Invitation' }
      },
      {
        path: 'forgot-password',
        loadComponent: () => import('./features/auth/forgot-password/forgot-password').then(m => m.ForgotPasswordComponent),
        data: { title: 'Forgot Password' }
      },
      {
        path: 'reset-password',
        loadComponent: () => import('./features/auth/reset-password/reset-password').then(m => m.ResetPasswordComponent),
        data: { title: 'Reset Password' }
      },
      {
        path: 'verify-email',
        loadComponent: () => import('./features/auth/verify-email/verify-email').then(m => m.VerifyEmailComponent),
        data: { title: 'Verify Email' }
      },
      {
        path: 'resend-verification',
        loadComponent: () => import('./features/auth/resend-verification/resend-verification').then(m => m.ResendVerificationComponent),
        data: { title: 'Resend Verification' }
      },
    ],
  },

  // App routes (protected, lazy loaded)
  {
    path: '',
    component: AppLayoutComponent,
    canActivate: [authGuard, emailVerifiedGuard],
    children: [
      {
        path: 'dashboard',
        loadComponent: () => import('./features/dashboard/dashboard.component').then(m => m.DashboardComponent),
        data: { title: 'Dashboard' }
      },

      // Log Receivers
      {
        path: 'log-receivers',
        children: [
          {
            path: '',
            loadComponent: () => import('./features/log-receivers/log-receivers-list/log-receivers-list.component').then(m => m.LogReceiversListComponent),
            data: { title: 'Receivers' }
          },
          {
            path: 'create',
            loadComponent: () => import('./features/log-receivers/log-receiver-form/log-receiver-form.component').then(m => m.LogReceiverFormComponent),
            data: { title: 'Create Receiver' }
          },
          {
            path: ':receiverId/edit',
            loadComponent: () => import('./features/log-receivers/log-receiver-form/log-receiver-form.component').then(m => m.LogReceiverFormComponent),
            data: { title: 'Edit Receiver' }
          },
        ],
      },

      // Log Streams
      {
        path: 'log-streams',
        children: [
          {
            path: '',
            loadComponent: () => import('./features/log-streams/log-streams-list/log-streams-list.component').then(m => m.LogStreamsListComponent),
            data: { title: 'Streams' }
          },
          {
            path: 'create',
            loadComponent: () => import('./features/log-streams/log-stream-form/log-stream-form.component').then(m => m.LogStreamFormComponent),
            data: { title: 'Create Stream' }
          },
          {
            path: ':streamId/edit',
            loadComponent: () => import('./features/log-streams/log-stream-form/log-stream-form.component').then(m => m.LogStreamFormComponent),
            data: { title: 'Edit Stream' }
          },
        ],
      },

      // Access tokens
      {
        path: 'access-tokens',
        children: [
          {
            path: '',
            loadComponent: () => import('./features/access-tokens/access-tokens-list/access-tokens-list.component').then(m => m.AccessTokensListComponent),
            data: { title: 'Access tokens' }
          },
          {
            path: 'create',
            loadComponent: () => import('./features/access-tokens/access-token-form/access-token-form.component').then(m => m.AccessTokenFormComponent),
            data: { title: 'Create access token' }
          },
          {
            path: ':tokenId/edit',
            loadComponent: () => import('./features/access-tokens/access-token-form/access-token-form.component').then(m => m.AccessTokenFormComponent),
            data: { title: 'Edit access token' }
          },
        ],
      },

      // Live Tail (heaviest component with ECharts)
      {
        path: 'live-tail',
        loadComponent: () => import('./features/live-tail/live-tail.component').then(m => m.LiveTailComponent),
        data: { title: 'Tail' }
      },
      {
        path: 'live-tail/:streamId',
        loadComponent: () => import('./features/live-tail/live-tail.component').then(m => m.LiveTailComponent),
        data: { title: 'Tail' }
      },

      {
        path: 'usage',
        loadComponent: () => import('./features/usage/usage.component').then(m => m.UsageComponent),
        data: { title: 'Usage' }
      },

      // Invitations
      {
        path: 'invitations',
        loadComponent: () => import('./pages/invitations/invitations').then(m => m.Invitations),
        data: { title: 'Invitations' }
      },

      // Settings
      {
        path: 'settings',
        loadComponent: () => import('./features/settings/settings.component').then(m => m.SettingsComponent),
        data: { title: 'Settings' },
        children: [
          { path: '', redirectTo: 'profile', pathMatch: 'full' },
          {
            path: 'profile',
            loadComponent: () => import('./features/settings/tabs/profile-tab.component').then(m => m.ProfileTabComponent),
            data: { title: 'Profile' }
          },
          {
            path: 'account',
            loadComponent: () => import('./features/settings/tabs/account-tab.component').then(m => m.AccountTabComponent),
            data: { title: 'Account' }
          },
          {
            path: 'team',
            loadComponent: () => import('./features/settings/tabs/team-tab.component').then(m => m.TeamTabComponent),
            data: { title: 'Team' }
          },
          {
            path: 'security',
            loadComponent: () => import('./features/settings/tabs/security-tab.component').then(m => m.SecurityTabComponent),
            data: { title: 'Security' }
          },
          {
            path: 'sources',
            loadComponent: () => import('./features/settings/tabs/sources-tab.component').then(m => m.SourcesTabComponent),
            data: { title: 'Sources' }
          },
          {
            path: 'integrations',
            loadComponent: () => import('./features/settings/tabs/integrations-tab.component').then(m => m.IntegrationsTabComponent),
            data: { title: 'Integrations' }
          },
          {
            path: 'plans',
            loadComponent: () => import('./features/settings/tabs/billing-tab.component').then(m => m.BillingTabComponent),
            data: { title: 'Choose a plan', planPicker: true }
          },
          {
            path: 'billing',
            loadComponent: () => import('./features/settings/tabs/billing-tab.component').then(m => m.BillingTabComponent),
            data: { title: 'Billing' }
          },
        ]
      },
    ],
  },

  // Wildcard
  { path: '**', loadComponent: () => import('./features/not-found/not-found.component').then(m => m.NotFoundComponent), title: 'Page not found — Fluxtail' },
];
